import { useRef, useEffect } from 'react';
import { Users, MicOff, VideoOff } from 'lucide-react';
import { useTranslation } from '../../i18n';

/**
 * Single video participant tile
 * Shows video stream or avatar placeholder
 */
const VideoParticipant = ({
  participant,
  videoTrack,
  isLocal = false,
  className = '',
  showName = true,
}) => {
  const { t } = useTranslation();
  const videoRef = useRef(null);

  // Attach video track to video element
  useEffect(() => {
    if (videoTrack?.track && videoRef.current) {
      videoTrack.track.attach(videoRef.current);
      return () => {
        videoTrack.track.detach(videoRef.current);
      };
    }
  }, [videoTrack]);

  const hasVideo = videoTrack?.track && !videoTrack.isMuted;
  const isMuted = participant?.isMicrophoneEnabled === false;
  const participantName = participant?.name || participant?.identity || t('video.participant');

  // Determine display name
  const displayName = isLocal
    ? participantName
    : (participant?.metadata ? JSON.parse(participant.metadata)?.role === 'teacher' : false)
      ? t('video.teacher')
      : participantName;

  return (
    <div
      className={`relative bg-brand-cream rounded-lg overflow-hidden border-2 border-brand-primary ${className}`}
    >
      {hasVideo ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={isLocal}
          className="w-full h-full object-cover"
          style={{ transform: isLocal ? 'scaleX(-1)' : 'none' }}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-brand-cream">
          <div className="text-center">
            <div className="w-8 h-8 bg-brand-light rounded-full mx-auto mb-1 flex items-center justify-center">
              <Users size={16} className="text-brand-primary" />
            </div>
            {showName && (
              <p className="text-[10px] text-brand-primary font-medium truncate max-w-[80px]">
                {displayName}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Status indicators overlay */}
      <div className="absolute bottom-1 left-1 flex gap-1">
        {isMuted && (
          <div className="bg-red-500 rounded-full p-0.5">
            <MicOff size={10} className="text-white" />
          </div>
        )}
        {!hasVideo && videoTrack && (
          <div className="bg-red-500 rounded-full p-0.5">
            <VideoOff size={10} className="text-white" />
          </div>
        )}
      </div>

      {/* Name overlay when video is playing */}
      {hasVideo && showName && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1">
          <p className="text-[10px] text-white font-medium truncate">
            {displayName}
          </p>
        </div>
      )}

      {/* Local indicator */}
      {isLocal && (
        <div className="absolute top-1 right-1 bg-brand-primary text-white text-[8px] px-1.5 py-0.5 rounded">
          {t('video.you')}
        </div>
      )}
    </div>
  );
};

export default VideoParticipant;
